import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'reactstrap';
import styles from './RubricQuestionPickerModal.module.scss';
import PickAssessmentPhase from './PickAssessmentPhase';
import BrowseQuestionsPhase from './BrowseQuestionsPhase';
import ModalLoading from './ModalLoading';
import ModalLoadError from './ModalLoadError';
/** Decay sum above this is shown in "Most used" before "Show all". */
const MOST_USED_DECAY_MIN = 0.12;

function assessmentMatchesSearch(group, rawQuery) {
  const q = rawQuery.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const blob = [group.primaryName, group.key, ...(group.aliases || [])]
    .join(' ')
    .toLowerCase();
  return blob.includes(q);
}

export default function RubricQuestionPickerModal({
  isOpen,
  onClose,
  onAddQuestions,
  onAddQuestionsWithSets,
  /** @type {'addQuestions' | 'addSource'} */
  pickerMode = 'addQuestions',
  assessmentGroups,
  loading,
  loadError,
}) {
  const [activeAssessmentKey, setActiveAssessmentKey] = useState(null);
  const [phase, setPhase] = useState('pick');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllBelowCutoff, setShowAllBelowCutoff] = useState(false);

  const activeAssessment = useMemo(
    () => assessmentGroups.find((g) => g.key === activeAssessmentKey) ?? null,
    [assessmentGroups, activeAssessmentKey]
  );

  const searching = searchQuery.trim().length > 0;
  const filtered = useMemo(
    () =>
      assessmentGroups.filter((g) => assessmentMatchesSearch(g, searchQuery)),
    [assessmentGroups, searchQuery]
  );

  const { topTier, lowTier } = useMemo(() => {
    const top = filtered.filter(
      (g) => (g.exponentialDecayScore ?? 0) >= MOST_USED_DECAY_MIN
    );
    const low = filtered.filter(
      (g) => (g.exponentialDecayScore ?? 0) < MOST_USED_DECAY_MIN
    );
    return { topTier: top, lowTier: low };
  }, [filtered]);

  const canCollapseLowTier =
    !searching && topTier.length > 0 && lowTier.length > 0;
  const showLowTierList =
    searching || !canCollapseLowTier || showAllBelowCutoff;

  useEffect(() => {
    setPhase('pick');
    setActiveAssessmentKey(null);
    setSearchQuery('');
    setShowAllBelowCutoff(false);
  }, [assessmentGroups]);

  useEffect(() => {
    if (!isOpen) {
      setPhase('pick');
      setActiveAssessmentKey(null);
      setSearchQuery('');
      setShowAllBelowCutoff(false);
    }
  }, [isOpen]);

  const handleChooseAssessment = useCallback((assessmentKey) => {
    setActiveAssessmentKey(assessmentKey);
    setPhase('browse');
  }, []);

  const handleBackToAssessments = useCallback(() => {
    setPhase('pick');
  }, []);

  const handleAddQuestions = useCallback(
    (questions) => {
      try {
        onAddQuestions?.(questions);
      } finally {
        onClose?.();
      }
    },
    [onAddQuestions, onClose]
  );

  const handleAddQuestionsWithSets = useCallback(
    (questions) => {
      try {
        onAddQuestionsWithSets?.(questions);
      } finally {
        onClose?.();
      }
    },
    [onAddQuestionsWithSets, onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen
      size="xl"
      className={styles['question-picker-modal-root']}
      centered
      toggle={onClose}
    >
      {loading ? <ModalLoading onClose={onClose} /> : <></>}
      {loading && <p>Loading available questions…</p>}
      {loadError ? (
        <ModalLoadError onClose={onClose} />
      ) : (
        <>
          {phase === 'pick' && (
            <PickAssessmentPhase
              onClose={onClose}
              assessmentGroups={assessmentGroups}
              filtered={filtered}
              searching={searching}
              canCollapseLowTier={canCollapseLowTier}
              topTier={topTier}
              lowTier={lowTier}
              showLowTierList={showLowTierList}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onChooseAssessment={handleChooseAssessment}
              onShowAllBelowCutoff={() => setShowAllBelowCutoff(true)}
            />
          )}

          {phase === 'browse' && (
            <BrowseQuestionsPhase
              onClose={onClose}
              activeAssessment={activeAssessment}
              onBackToAssessments={handleBackToAssessments}
              onAddQuestions={handleAddQuestions}
              onAddQuestionsWithSets={handleAddQuestionsWithSets}
              pickerMode={pickerMode}
            />
          )}
        </>
      )}
    </Modal>
  );
}
